import "./styles.scss";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

import { AppContainer, Button, PoolResults } from "../../components";
import outOfTimeImg from "../../../assets/out_of_time.png";

import { usePool } from "../../../hooks/usePool";
import { useUsers } from "../../../hooks/useUsers";

import { formatToMexicoUTC, getDayInMexico } from "../../../utils";

import { dispatch, Events } from "../../../redux/dispatch";

import { TiWarning } from "react-icons/ti";
import { HiOutlineRefresh } from "react-icons/hi";

const POOL_CACHE_TTL = 3 * 24 * 60 * 60 * 1000;

const getPoolCacheKey = (poolId) => `pool:${poolId}`;

const removePoolCache = (poolId) => {
  try {
    localStorage.removeItem(getPoolCacheKey(poolId));
  } catch (_) {}
};

const readPoolCache = (poolId) => {
  try {
    const cache = JSON.parse(localStorage.getItem(getPoolCacheKey(poolId)));

    if (
      !cache ||
      !Array.isArray(cache.results) ||
      !Number.isFinite(cache.createdAt) ||
      !cache.results.every(
        (result) =>
          result &&
          result.id !== undefined &&
          ["L", "T", "V"].includes(result.result),
      ) ||
      Date.now() - cache.createdAt >= POOL_CACHE_TTL
    ) {
      removePoolCache(poolId);
      return [];
    }

    return cache.results;
  } catch (_) {
    removePoolCache(poolId);
    return [];
  }
};

const savePoolCache = (poolId, matches) => {
  const results = matches
    .filter((match) => match.result)
    .map(({ id, result }) => ({ id, result }));

  if (!results.length) {
    removePoolCache(poolId);
    return;
  }

  const key = getPoolCacheKey(poolId);
  let createdAt = Date.now();

  try {
    const previous = JSON.parse(localStorage.getItem(key));
    if (previous?.createdAt && Date.now() - previous.createdAt < POOL_CACHE_TTL) {
      createdAt = previous.createdAt;
    }

    localStorage.setItem(key, JSON.stringify({ createdAt, results }));
  } catch (_) {}
};

export const ViewPool = () => {
  const mainPool = useSelector((state) => state.mainPool);
  const user = useSelector((state) => state.user);

  const [matchesWithResults, setMachesWithResults] = useState([]);

  const { createUserPool, getMainPool } = usePool();
  const { getUser } = useUsers();

  useEffect(() => {
    if (!mainPool?.matches) return;
    if (user.pool) {
      removePoolCache(mainPool.id);
      return;
    }

    const cachedResults = new Map(
      readPoolCache(mainPool.id).map(({ id, result }) => [id, result]),
    );

    setMachesWithResults(
      mainPool.matches.map((match) =>
        cachedResults.has(match.id)
          ? { ...match, result: cachedResults.get(match.id) }
          : match,
      ),
    );
  }, [mainPool, user.pool]);

  useEffect(() => {
    if (!mainPool?.started) getMainPool();
  }, []);

  const poolResultUpdate = (dataResult) => {
    let newMatches = [...matchesWithResults];

    newMatches[dataResult.index] = {
      ...newMatches[dataResult.index],
      result: dataResult.result,
    };

    setMachesWithResults(newMatches);
    savePoolCache(mainPool.id, newMatches);
  };

  const isPoolValid =
    matchesWithResults.length > 0 &&
    matchesWithResults.every((match) => Boolean(match.result));

  const createPool = () => {
    if (mainPool?.started) {
      return (
        <div className='empty-pool-container'>
          <img className='img' src={outOfTimeImg} alt='out of time' />
          <span className='text-xl'>No llenaste tu quiniela a tiempo</span>
        </div>
      );
    }

    if (mainPool?.matches) {
      return matchesWithResults.map((match, index) => (
        <PoolResults
          key={index}
          match={match}
          index={index}
          clear={!match.result}
          disable={!user.active}
          onUpdate={poolResultUpdate}
        />
      ));
    }
  };

  const createBlockPool = () => {
    if (user?.pool?.matches) {
      return user.pool.matches.map((match, index) => (
        <PoolResults
          key={index}
          match={match}
          index={index}
          disable
          started={mainPool?.started}
          poolId={mainPool?.id}
        />
      ));
    }
  };

  const updateUserPool = async () => {
    await createUserPool(matchesWithResults);
    removePoolCache(mainPool.id);
    getUser();
    getUser();
    dispatch(Events.SHOW_ALERT, {
      message: "¡Quiniela enviada con éxito!",
      type: "SUCCESS",
    });
    // setTimeout(async () => {
    //   await getUser();
    // }, 1000);
  };

  return (
    <AppContainer>
      {!mainPool || !mainPool.matches ? (
        <div className='empty-pool-container'>
          <img className='img' src={outOfTimeImg} alt='quiniela' />
          <span className='text-white text-center text-2xl'>
            No tienes quiniela por llenar
          </span>
        </div>
      ) : (
        <div className='flex flex-col w-full h-full items-center z-20'>
          <span className='text-white text-2xl mt-5'>Mi quiniela</span>

          <span className='text-sm mt-1 opacity-80'>
            Total de partidos: {mainPool?.matches?.length || 0}{" "}
          </span>

          {mainPool?.startAt?._seconds && (
            <span className='text-[11px] mt-1 opacity-80'>
              Fecha de inicio:{" "}
              <span className='text-[11px] mt-1 opacity-80'>
                {getDayInMexico(new Date(mainPool.startAt._seconds * 1000))}{" "}
                {formatToMexicoUTC(new Date(mainPool.startAt._seconds * 1000))}
              </span>
            </span>
          )}

          {!user.active && (
            <div className='mt-4'>
              <div className='flex'>
                <TiWarning className='text-yellow-400' />
                <span className='text-[12px] ml-2'>
                  Tu usuario está desactivado
                </span>
              </div>
              <div className='flex mt-3 items-center justify-center text-white'>
                <button
                  className='text-white text-[12px] underline'
                  onClick={getUser}>
                  Actualizar
                </button>
                <HiOutlineRefresh className='h-[14px] w-[14px] ml-1' />
              </div>
            </div>
          )}

          <div className='flex flex-col w-[86%] flex-1 mt-5 overflow-auto '>
            {user.pool ? createBlockPool() : createPool()}
          </div>
          <div className='mb-10 h-[100px] flex items-end'>
            {!user.pool && user?.active && (
              <Button
                label='Enviar'
                onClick={() => {
                  if (isPoolValid) updateUserPool();
                }}
                disabled={!isPoolValid}
              />
            )}
          </div>
        </div>
      )}
    </AppContainer>
  );
};
